import { ModuleWrapper } from '@/components'
import type { LinkListItem } from '@/components_deprecated'
import { LinkList } from '@/components_deprecated'
import { useBreadcrumbs } from '@/utils/hooks'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useEffect, useMemo } from 'react'
import { isMobile } from 'react-device-detect'
import { Outlet } from 'react-router'
import styled from 'styled-components'
import { useCompositeModules, useModuleConfigs } from './hooks'

const Content = styled.div`
  display: grid;
  grid-template-columns: auto 3fr;
  grid-template-rows: auto auto;
  gap: 2rem;

  ${props => props.theme.media.mobile} {
    display: flex;
    flex-direction: column;
    gap: 0;

    & > div + div {
      margin-top: 1rem;
    }
  }
`
const Links = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: max-content;
  grid-column: 1;
  grid-row: 1 / 3;
`

interface CompanySettingsProps {}

export const CompanySettings: React.FC<CompanySettingsProps> = () => {
  const translations = useTranslate({
    companySettings: 'settings.company-settings',
    activities: 'common.activities',
    issues: 'common.issue',
    infringements: 'common.infringements',
    absenceTypes: 'common.absences',
    absence: 'common.absence',
    timesheets: 'common.timesheets',
    terminalNetwork: 'common.terminal',
    userTypes: 'common.user-types',
    salary: 'settings.salary-and-work-time',
    integrations: 'settings.integrations',
    dropdowns: 'common.dropdowns',
    userDocuments: 'common.user-documents',
    userEquipment: 'common.user-equipment',
    notifications: 'common.notifications',
    checklists: 'common.checklists',
    export: 'common.exports-settings',
    tableSettings: 'settings.tables',
    routePlanner: 'common.route-planner',
    handbook: 'common.handbook',
    vehicleFleet: 'vehicles.fleet-overview',
    fats: 'common.fats',
    fatsBeta: 'common.fats-beta',

    salaryExport: 'common.salary-export',
  })

  const { setOverride, overrides } = useBreadcrumbs()
  const { active } = useModuleConfigs()

  const { mainModuleActive } = useCompositeModules()

  const links = useMemo<LinkListItem[]>(
    () => [
      {
        text: translations.companySettings,
        href: '',
        matchBy: /\/settings\/?$/,
      },
      {
        text: translations.userTypes,
        href: 'user-types',
      },
      {
        text: translations.dropdowns,
        href: 'dropdowns',
      },
      {
        text: translations.salary,
        href: 'salary',
      },
      {
        text: translations.userDocuments,
        href: 'user-documents',
      },
      {
        text: translations.userEquipment,
        href: 'user-equipment',
      },
      {
        text: translations.checklists,
        href: 'checklist-templates',
      },
      {
        text: translations.integrations,
        href: 'integrations',
        hasModule: active.activities,
      },
    ],
    [
      active.activities,
      translations.checklists,
      translations.companySettings,
      translations.dropdowns,
      translations.integrations,
      translations.salary,
      translations.userDocuments,
      translations.userEquipment,
      translations.userTypes,
    ]
  )

  const utilLinks = useMemo<LinkListItem[]>(
    () => [
      {
        text: translations.export,
        href: '/settings/export',
        hasModule: mainModuleActive,
      },
      {
        text: translations.salaryExport,
        href: '/settings/salary-export',
        hasModule: mainModuleActive,
      },
      {
        text: translations.fatsBeta,
        href: '/settings/fats',
        hasModule: mainModuleActive,
      },
    ],
    [
      mainModuleActive,
      translations.export,
      translations.fatsBeta,
      translations.salaryExport,
    ]
  )

  const moduleLinks = useMemo<LinkListItem[]>(
    () => [
      {
        text: translations.activities,
        href: '/settings/activities',
        hasModule: active.activities,
      },
      {
        text: translations.terminalNetwork,
        href: '/settings/terminal-network',
        hasModule: active.terminal,
      },
      {
        text: translations.issues,
        href: '/settings/issues',
        hasModule: active.issues,
      },
      {
        text: translations.infringements,
        href: '/settings/infringements',
        hasModule: active.infringements,
      },
      {
        text: translations.routePlanner,
        href: '/settings/route-planner',
        hasModule: active.routePlanner,
      },
      {
        text: translations.timesheets,
        href: '/settings/time-entries',
        hasModule: active.timesheets,
      },
      {
        text: translations.absence,
        href: '/settings/absence',
        hasModule: active.absences,
      },
      {
        text: translations.vehicleFleet,
        href: '/settings/vehicles',
        hasModule: active.vehicles,
      },
      {
        text: translations.handbook,
        href: '/settings/handbook',
        hasModule: active.handbook,
      },
    ],
    [
      active.absences,
      active.activities,
      active.handbook,
      active.infringements,
      active.issues,
      active.routePlanner,
      active.terminal,
      active.timesheets,
      active.vehicles,
      translations.absence,
      translations.activities,
      translations.handbook,
      translations.infringements,
      translations.issues,
      translations.routePlanner,
      translations.terminalNetwork,
      translations.timesheets,
      translations.vehicleFleet,
    ]
  )

  useEffect(() => {
    if (overrides['settings']) return
    setOverride('settings', translations.companySettings)
  }, [overrides, setOverride, translations.companySettings])

  return (
    <ModuleWrapper title={translations.companySettings}>
      <Content>
        <Links>
          <LinkList items={links} expandable={isMobile} />
          <LinkList items={utilLinks} expandable={isMobile} />
          <LinkList items={moduleLinks} expandable={isMobile} />
        </Links>

        <Outlet />
      </Content>
    </ModuleWrapper>
  )
}
